import { useEffect, useMemo, useState } from 'react'
import type { AppSettings } from '../types/settings'
import { KEY_OPTIONS, keyCodeLabel } from '../types/settings'
import type { KeyBindings } from '../hooks/useMorseInput'
import { CloseIcon, KeyboardIcon } from './Icons'

interface SettingsPanelProps {
  open: boolean
  settings: AppSettings
  onChange: (settings: AppSettings) => void
  onClose: () => void
}

type BindingName = keyof KeyBindings

const BINDING_LABELS: Record<BindingName, string> = {
  dit: '短点（・）',
  dah: '長点（－）',
  straight: '電鍵',
}

export function SettingsPanel({ open, settings, onChange, onClose }: SettingsPanelProps) {
  const [capturing, setCapturing] = useState<BindingName | null>(null)
  const [activeCodes, setActiveCodes] = useState<Set<string>>(() => new Set())

  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (!capturing && event.code === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (capturing) {
        event.preventDefault()
        event.stopPropagation()
        if (event.code === 'Escape') {
          setCapturing(null)
          return
        }
        onChange({
          ...settings,
          keyBindings: { ...settings.keyBindings, [capturing]: event.code },
        })
        setCapturing(null)
      }
      setActiveCodes((codes) => new Set(codes).add(event.code))
    }
    const onKeyUp = (event: KeyboardEvent) => {
      setActiveCodes((codes) => {
        const next = new Set(codes)
        next.delete(event.code)
        return next
      })
    }
    window.addEventListener('keydown', onKeyDown, true)
    window.addEventListener('keyup', onKeyUp, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown, true)
      window.removeEventListener('keyup', onKeyUp, true)
    }
  }, [capturing, onChange, open, settings])

  const duplicatePaddleKey = settings.keyBindings.dit === settings.keyBindings.dah
  const bindings = settings.mode === 'paddle' ? (['dit', 'dah'] as const) : (['straight'] as const)
  const customOptions = useMemo(() => {
    const known = new Set(KEY_OPTIONS.map(([value]) => value))
    return Object.values(settings.keyBindings).filter((code) => !known.has(code as never))
  }, [settings.keyBindings])

  if (!open) return null

  const patch = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    onChange({ ...settings, [key]: value })
  }

  return (
    <div className="settings-layer" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose()
    }}>
      <aside className="settings-panel" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <div className="settings-header">
          <div><span className="eyebrow">YOUR KEYER</span><h2 id="settings-title">入力設定</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="設定を閉じる"><CloseIcon /></button>
        </div>

        <div className="settings-scroll">
          <section className="settings-group">
            <div className="settings-label"><span>01</span><div><h3>電鍵のタイプ</h3><p>実際に使う機器に合わせて選びます</p></div></div>
            <div className="mode-choice">
              <button type="button" className={settings.mode === 'paddle' ? 'selected' : ''} onClick={() => patch('mode', 'paddle')}>
                <span className="paddle-symbol"><i>·</i><i>—</i></span>
                <strong>パドル式</strong><small>左右で短点・長点</small>
              </button>
              <button type="button" className={settings.mode === 'straight' ? 'selected' : ''} onClick={() => patch('mode', 'straight')}>
                <span className="straight-symbol"><i /></span>
                <strong>縦振れ式</strong><small>押す長さで判定</small>
              </button>
            </div>
          </section>

          <section className="settings-group">
            <div className="settings-label"><span>02</span><div><h3>キーの割り当て</h3><p>一覧から選ぶか、使いたいキーを押して登録</p></div></div>
            <div className="binding-list">
              {bindings.map((name) => {
                const code = settings.keyBindings[name]
                return (
                  <div className="binding-row" key={name}>
                    <label htmlFor={`binding-${name}`}>{BINDING_LABELS[name]}</label>
                    <select
                      id={`binding-${name}`}
                      value={code}
                      onChange={(event) => onChange({
                        ...settings,
                        keyBindings: { ...settings.keyBindings, [name]: event.target.value },
                      })}
                    >
                      {KEY_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
                      {customOptions.map((value) => <option value={value} key={value}>{keyCodeLabel(value)}</option>)}
                    </select>
                    <button
                      type="button"
                      className={`capture-button ${capturing === name ? 'is-capturing' : ''}`}
                      aria-label={`${BINDING_LABELS[name]}のキーを${capturing === name ? '入力中' : '押して登録'}`}
                      onClick={() => setCapturing(capturing === name ? null : name)}
                    >
                      <KeyboardIcon size={18} />
                      {capturing === name ? 'キーを押す…' : '押して登録'}
                    </button>
                  </div>
                )
              })}
            </div>
            {duplicatePaddleKey && settings.mode === 'paddle' && <p className="settings-warning">短点と長点には別々のキーを割り当ててください。</p>}
            <div className="hid-note">
              <span className="connection-dot" />
              <div><strong>USB / BLE HID 対応</strong><p>キーボードとして接続される電鍵は、そのままキー登録できます。</p></div>
            </div>
            <div className="input-monitor">
              <span>入力テスト</span>
              {bindings.map((name) => (
                <kbd className={activeCodes.has(settings.keyBindings[name]) ? 'active' : ''} key={name}>
                  {keyCodeLabel(settings.keyBindings[name])}
                </kbd>
              ))}
              <small>キーを押すと点灯します</small>
            </div>
          </section>

          <section className="settings-group">
            <div className="settings-label"><span>03</span><div><h3>キーイングの音</h3><p>練習しやすい速さと音に調整</p></div></div>
            <label className="range-row">
              <span><strong>速度</strong><output>{settings.wpm} WPM</output></span>
              <input type="range" min="5" max="30" step="1" value={settings.wpm} onChange={(event) => patch('wpm', Number(event.target.value))} />
              <small><span>ゆっくり</span><span>はやい</span></small>
            </label>
            <label className="range-row">
              <span><strong>音の高さ</strong><output>{settings.frequency} Hz</output></span>
              <input type="range" min="400" max="900" step="25" value={settings.frequency} onChange={(event) => patch('frequency', Number(event.target.value))} />
              <small><span>低い</span><span>高い</span></small>
            </label>
            <label className="range-row">
              <span><strong>音量</strong><output>{Math.round(settings.volume * 100)}%</output></span>
              <input type="range" min="0" max="1" step="0.05" value={settings.volume} onChange={(event) => patch('volume', Number(event.target.value))} />
              <small><span>消音</span><span>大きい</span></small>
            </label>
            <label className="switch-row">
              <span><strong>モールスのヒント</strong><small>練習中に符号を表示する</small></span>
              <input type="checkbox" checked={settings.showHint} onChange={(event) => patch('showHint', event.target.checked)} />
              <i aria-hidden="true" />
            </label>
          </section>
        </div>

        <div className="settings-footer"><button className="primary-button" type="button" onClick={onClose}>この設定で練習する</button></div>
      </aside>
    </div>
  )
}
